import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getDocumentsState, getFilesIds, getIsSingleFile, getLiveTextStatus } from 'store';
import { DocumentStates } from 'types';
import { useFlags } from 'launchdarkly-react-client-sdk';

interface NoLiveTextAlertProps {
  disabled: boolean;
}

export const useNoLiveTextAlert = ({ disabled }: NoLiveTextAlertProps) => {
  const isSingleFile = useSelector(getIsSingleFile);
  const liveTextStatus = useSelector(getLiveTextStatus);
  const [alertText, setAlertText] = useState<string>('');
  const eitherFileMissingLiveText = liveTextStatus.source || liveTextStatus.target;
  const bothFilesMissingLiveText = liveTextStatus.source && liveTextStatus.target && !isSingleFile;
  const filesId = useSelector(getFilesIds);
  const filesLoaded = filesId.sampleFileId || (!isSingleFile && filesId.masterFileId);
  const documentsState = useSelector(getDocumentsState);
  const bothDocumentsLoaded =
    (!isSingleFile &&
      documentsState.source === DocumentStates.LOADED &&
      documentsState.target === DocumentStates.LOADED) ||
    (isSingleFile && documentsState.target === DocumentStates.LOADED);

  const featureFlags = useFlags();
  const showOcr = featureFlags.showOcr;

  useEffect(() => {
    if (!filesLoaded) {
      setAlertText('');
    } else if (eitherFileMissingLiveText) {
      let boldText;
      if (bothFilesMissingLiveText) {
        boldText = 'Both of your documents ';
      } else if (isSingleFile) {
        boldText = 'Your document ';
      } else {
        boldText = liveTextStatus.source ? 'Your Source document ' : 'Your New document ';
      }
      const ocrMessage = showOcr
        ? `${boldText}${bothFilesMissingLiveText ? 'do' : 'does'} not contain live text. Apply OCR to enable a ${isSingleFile ? 'spelling' : 'text'} inspection.`
        : `${boldText}${bothFilesMissingLiveText ? 'do' : 'does'} not contain live text.`;
      setAlertText(ocrMessage);
    } else {
      setAlertText('');
    }
  }, [liveTextStatus.source, liveTextStatus.target, isSingleFile, bothFilesMissingLiveText, filesLoaded, showOcr]);

  return bothDocumentsLoaded && disabled ? alertText : '';
};
